// src/components/resources/calculators/video-dropoff/components/charts/DropoffChart.tsx

/**
 * @file Dropoff Chart Component
 * @description Chart component for displaying video dropoff data
 */ 

import {
  Area,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
  ReferenceLine,
  AreaChart,
  Tooltip,
} from '~/lib/charts/client'
import type { ChartScale, Breakpoint, LargestPostHookDrop, DropoffDataPoint } from '~/components/resources/calculators/video-dropoff/types'
import { CustomTooltip } from '~/components/resources/calculators/video-dropoff/components/charts/CustomTooltip'
import { formatTime } from '~/components/resources/calculators/video-dropoff/utils/formatters'

/**
 * Props for the DropoffChart component
 */
interface DropoffChartProps {
  /** Chart data prepared for rendering with proper typing */
  data: DropoffDataPoint[]
  /** Video length in seconds */
  videoLength: number
  /** Chart scale (linear or log) */
  chartScale: ChartScale
  /** Whether to exclude video starts from display */
  excludeVideoStarts: boolean
  /** Whether to show the largest post-hook drop marker */
  showLargestPostHookDrop: boolean
  /** Information about the largest post-hook drop */
  largestPostHookDrop: LargestPostHookDrop | null
  /** Breakpoints for reference lines */
  breakpoints: Breakpoint[]
}

/**
 * Dropoff chart component for video dropoff analysis
 * @param {DropoffChartProps} props - Component props
 * @returns {JSX.Element} Rendered dropoff chart
 */
export const DropoffChart = ({
  data,
  videoLength,
  chartScale,
  excludeVideoStarts,
  showLargestPostHookDrop,
  largestPostHookDrop,
  breakpoints
}: DropoffChartProps): JSX.Element => {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{ top: 30, right: 20, left: 25, bottom: 20 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgb(156, 163, 175)" opacity={0.2} />
        <XAxis 
          dataKey="timestamp"
          type="number"
          domain={
            excludeVideoStarts && data.length > 0
              ? [data[0]?.timestamp ?? 0, videoLength]
              : [0, videoLength]
          }
          tickFormatter={(value) => formatTime(value as number)}
          label={{ 
            value: "Video Timeline (seconds)", 
            position: "insideBottom", 
            offset: 0,
            style: { fill: 'rgb(107, 114, 128)', fontSize: '11px' },
            dy: 15
          }}
          stroke="rgb(156, 163, 175)"
          tickLine={false}
          tick={{ fontSize: 11 }}
        />
        <YAxis
          scale={chartScale}
          domain={
            excludeVideoStarts 
              ? chartScale === 'log'
                ? [1, 'auto']
                : ['dataMin', 'auto']
              : chartScale === 'log' 
                ? [1, 100] 
                : [0, 100]
          }
          tickFormatter={(value) => `${value}%`}
          allowDataOverflow={true}
          stroke="rgb(156, 163, 175)"
          tickLine={false}
          tick={{ fontSize: 11 }}
          width={40}
          label={{
            value: "Drop-off Rate (%)",
            angle: -90,
            style: { fill: 'rgb(107, 114, 128)', fontSize: '12px' },
            offset: 0,
            dx: -35
          }}
        />
        <Tooltip 
          content={<CustomTooltip videoLength={videoLength} activeView="dropoff" />}
          animationDuration={200}
          cursor={{ stroke: 'rgba(107, 114, 128, 0.5)', strokeWidth: 1, strokeDasharray: '5 5' }}
        />
        
        {/* Largest post-hook drop marker */}
        {showLargestPostHookDrop && largestPostHookDrop !== null && (
          <ReferenceLine 
            x={largestPostHookDrop.time} 
            stroke="rgba(220, 38, 38, 0.7)" 
            strokeWidth={2}
            label={{ 
              value: `Largest Post-Hook Drop (${largestPostHookDrop.dropoffRate.toFixed(1)}%)`, 
              position: "top", 
              fill: 'rgba(220, 38, 38, 0.8)',
              fontSize: 11
            }} 
          />
        )}
        
        <Area
          type="monotone"
          dataKey="y"
          stroke="rgb(124, 58, 237)"
          strokeWidth={2}
          fill="rgba(124, 58, 237, 0.2)"
          activeDot={{ r: 8, strokeWidth: 0, fill: 'rgb(124, 58, 237)' }}
          name="Drop-off Rate"
          animationDuration={1500}
          animationEasing="ease-in-out"
        />
        
        {/* Breakpoint markers */}
        {breakpoints.slice(excludeVideoStarts ? 1 : 0).map(breakpoint => {
          const timePosition = breakpoint.isPercentile
            ? (breakpoint.value / 100) * videoLength
            : breakpoint.value
          
          // Skip if this is exactly at time 0
          if (timePosition === 0) return null
          
          return (
            <ReferenceLine
              key={breakpoint.id}
              x={timePosition}
              stroke="rgba(79, 70, 229, 0.4)"
              strokeWidth={1}
              strokeDasharray="3 3"
            />
          )
        })}
      </AreaChart>
    </ResponsiveContainer>
  )
}
